import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, startOfWeek } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import WeekPicker from "../Pickers/WeekPicker.jsx";
import { getFitnessDataBySubjectID } from "../../../services/apiCalls";
import StepsOverview from "./StepsOverview.jsx";
import StepsAndDistanceBarChart from "./StepsAndDistanceBarChart.jsx";
import FloorsBarChart from "./FloorsBarChart.jsx";
import ElevationBarChart from "./ElevationBarChart.jsx";
import { captureAndDownloadCharts } from "../CaptureCharts/captureAndDownloadCharts.js";
import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function PhysicalActivityComponent({
  selectedUser,
  tabsValue,
  setLoading,
}) {
  const [fitnessData, setFitnessData] = useState([]);
  const stepsOverview = useRef(null);
  const stepsAndDistanceRef = useRef(null);
  const floorsRef = useRef(null);
  const elevationRef = useRef(null);

  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState(today);
  const [todayClickFunc, setTodayClickFunc] = useState(null);

  const { t } = useTranslation("statistics");

  useEffect(() => {
    const fetchUserFitnessData = async () => {
      try {
        const allFitnessData = await getFitnessDataBySubjectID(selectedUser);
        const formattedStepsData = allFitnessData.map((data) => ({
          date: format(new Date(data.date), "yyyy-MM-dd"),
          steps: data.steps,
          distance: data.distance,
          floors: data.floors,
          elevation: data.elevation,
        }));
        // console.log("All fitness data: ", allFitnessData);

        setFitnessData(formattedStepsData);
      } catch (error) {
        console.error(
          "Error when fetching fitness data in physical activity component ",
          error
        );
      }
    };
    fetchUserFitnessData();
  }, []);

  const handleTodayClickParent = (func) => {
    if (!todayClickFunc) {
      setTodayClickFunc(() => func);
    }
  };
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });

  const isCurrentWeek =
    weekStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    // console.log("Updated selected week:", week);
  };
  const weeksMonthsWithData = calculateWeeksAndMonthsWithData(fitnessData);
  // console.log(
  //   "weeksMonthsWithData in physicalActivityComponent: ",
  //   weeksMonthsWithData
  // );

  const weekStartISO = (d) =>
    format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const weeksWithDataSet = useMemo(() => {
    const s = new Set();
    for (const row of fitnessData) {
      const day = new Date(row.date); // row.date is 'yyyy-MM-dd'
      s.add(weekStartISO(day));
    }
    return s;
  }, [fitnessData]);

  const hasAnyData = fitnessData.length > 0;
  const todayWeekISO = weekStartISO(today);
  const todayWeekHasData = weeksWithDataSet.has(todayWeekISO);

  const showTodayButton = !isCurrentWeek && (!hasAnyData || todayWeekHasData);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WeekPicker
          today={today}
          currentCompWeek={currentWeek}
          sendDataToParent={handleTodayClickParent}
          onWeekChange={handleWeekChange}
          weeksWithData={weeksMonthsWithData.weeksWithData}
          monthsWithData={weeksMonthsWithData.monthsWithData}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() =>
              captureAndDownloadCharts(
                "Physical",
                tabsValue,
                { stepsOverview, stepsAndDistanceRef, floorsRef, elevationRef },
                setLoading
              )
            }
            sx={{
              maxWidth: "210px",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
          >
            {t("btn.downloadAllCharts")}
          </Button>
        </Box>

        {showTodayButton ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                maxWidth: "210px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "white",
                  color: "rgb(2, 178, 175)",
                },
              }}
              onClick={todayClickFunc}
            >
              {t("btn.today")}
            </Button>
          </Box>
        ) : (
          <Button
            sx={{
              visibility: "hidden",
            }}
          >
            {t("btn.today")}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StepsOverview
            fitnessData={fitnessData}
            selectedWeek={currentWeek}
            stepsOverview={stepsOverview}
          />
          <StepsAndDistanceBarChart
            fitnessData={fitnessData}
            selectedWeek={currentWeek}
            stepsAndDistanceRef={stepsAndDistanceRef}
          />
          <FloorsBarChart
            fitnessData={fitnessData}
            selectedWeek={currentWeek}
            floorsRef={floorsRef}
          />
          <ElevationBarChart
            fitnessData={fitnessData}
            selectedWeek={currentWeek}
            elevationRef={elevationRef}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
